import {
  compact,
  defaults,
  flow,
  fromPairs,
  get,
  getOr,
  hasIn,
  isArray,
  join,
  map,
  mergeAll,
  merge,
  omit,
  omitBy,
  set,
  toPairs,
  values,
  replace,
  trim,
  find,
} from 'lodash/fp';

import { stringFormatter } from 'helpers/i18n';
import getMapper from 'helpers/mapper';

const labels = {
  airbag: __('search.filters.airbag'),
  bodyType: __('search.filters.bodyType'),
  color: __('search.filters.color'),
  comfort: __('search.filters.comfort'),
  consumption: __('search.filters.consumption'),
  distance: __('search.filters.distance'),
  doors: __('search.filters.doors'),
  emissionSticker: __('search.filters.emissionSticker'),
  envStandard: __('search.filters.envStandard'),
  extras: __('search.filters.extras'),
  fuelType: __('search.filters.fuelType'),
  interior: __('search.filters.interior'),
  light: __('search.filters.light'),
  metallic: __('search.filters.color'),
  mileageRangeMax: __('search.filters.mileage.to'),
  mileageRangeMin: __('search.filters.mileage.from'),
  monthlyPriceRangeMax: __('search.filters.monthlyPrice.to'),
  monthlyPriceRangeMin: __('search.filters.monthlyPrice.from'),
  multimedia: __('search.filters.multimedia'),
  parkingSensors: __('search.filters.parkingSensors'),
  powerRangeMax: __('search.filters.power.to'),
  powerRangeMin: __('search.filters.power.from'),
  prevOwner: __('search.filters.prevOwner'),
  priceRangeMax: __('search.filters.price.to'),
  priceRangeMin: __('search.filters.price.from'),
  seatComfort: __('search.filters.seatComfort'),
  seats: __('search.filters.seats'),
  security: __('search.filters.security'),
  sellerType: __('search.filters.sellerType.title'),
  serviceHistory: __('search.filters.serviceHistory'),
  transmission: __('search.filters.transmission'),
  upholstery: __('search.filters.upholstery'),
  yearRangeMax: __('search.filters.year.to'),
  yearRangeMin: __('search.filters.year.from'),
  notDamaged: __('search.filters.notDamaged'),
  smoker: __('search.filters.smoker'),
  hasImages: __('search.filters.hasImages'),
};

const formatters = {
  mileageRangeMin: stringFormatter(__('search.filters.mileage.from')),
  mileageRangeMax: stringFormatter(__('search.filters.mileage.to')),
  powerRangeMin: stringFormatter(__('search.filters.power.from')),
  powerRangeMax: stringFormatter(__('search.filters.power.to')),
  priceRangeMin: stringFormatter(__('search.filters.price.from')),
  priceRangeMax: stringFormatter(__('search.filters.price.to')),
  monthlyPriceRangeMax: stringFormatter(__('search.filters.monthlyPrice.to')),
  monthlyPriceRangeMin: stringFormatter(__('search.filters.monthlyPrice.from')),
  yearRangeMin: stringFormatter(__('search.filters.year.from')),
  yearRangeMax: stringFormatter(__('search.filters.year.to')),
};

const defaultLabelFormatter = ({ path, value }) =>
  flow(get(path), (label) => [label, value], compact, join(': '))(labels);

const formatRangeLabel = (key, value) =>
  formatters[key]({ [key.replace(/Range(Min|Max)/, '')]: value });

const getFormForCheckboxList = flow(map(get(['form'])), mergeAll);

const makeQs = ([key, value]) => {
  if (isArray(value)) {
    return [key, getFormForCheckboxList(value)];
  }

  return [key, get(['form'], value)];
};

const getPreparedForm = (source, baseForm = {}) =>
  flow(
    toPairs,
    map(makeQs),
    fromPairs,
    values,
    mergeAll,
    merge(baseForm),
    defaults({}),
  )(source);

const moveCheckboxList = (path) => (acc, source, baseForm) => {
  const values = get(path, source);
  const groupedForm = getFormForCheckboxList(values);

  if (values) {
    const grouped = map(({ form, value }) => {
      const omitted = omitBy(
        (value, key) => hasIn([path, key], form),
        get([path], groupedForm),
      );

      const preparedForm = getPreparedForm(
        set([path], { form: { [path]: omitted } }, source),
        baseForm,
      );

      return {
        form: preparedForm,
        label: defaultLabelFormatter({ path, value }),
        name: path,
      };
    }, values);

    return set([path], grouped, acc);
  }

  return acc;
};

const movePath =
  (path, formatter = defaultLabelFormatter, options = {}) =>
  (acc, source, baseForm) => {
    const item = get(path, source);

    if (item) {
      const { value } = item;
      const name = getOr(path, ['name'], options);

      const preparedForm = getPreparedForm(omit(path, source), baseForm);

      return set(
        path,
        {
          form: preparedForm,
          label: formatter({ path, value }),
          name,
        },
        acc,
      );
    }

    return acc;
  };

const movePathBy = (path, getter = get, ...args) => getter(path, ...args);

const moveRange = (path) => (acc, source, baseForm) => {
  const item = get(path, source);

  if (item) {
    const { value } = item;
    const preparedForm = getPreparedForm(omit(path, source), baseForm);

    return set(
      path,
      {
        form: preparedForm,
        label: formatRangeLabel(path, value),
        name: path.replace(/(Min|Max)/, ''),
      },
      acc,
    );
  }

  return acc;
};

const getGeoLocation = (path) => (acc, source, baseForm) => {
  const value = get(['geoLocation', 'value'], source);

  if (value) {
    const preparedForm = getPreparedForm(
      omit([path, 'distance'], source),
      baseForm,
    );

    return set(
      path,
      {
        form: preparedForm,
        label: value,
        name: 'geoLocationPosition',
      },
      acc,
    );
  }

  return acc;
};

const getVehicleFilterLabel = (makeOptions) => (form, vehicle) => {
  const formValue = get(['vehicle', 0], form);
  const { make, model, subType } = formValue;

  const makeLabels = flow(
    toPairs,
    map(([, { label, value }]) => [value, label]),
    fromPairs,
  )(makeOptions);

  const makeLabel = get(make, makeLabels);
  const modelLabel = flow(
    get(['model', make, 'result']),
    find({ value: model }),
    get(['label']),
  )(vehicle);
  const subTypeLabel = flow(
    get(['subType', make, 'result', model, 'subtypes', subType]),
    replace(modelLabel, ''),
    trim,
  )(vehicle);

  return join(' ')([makeLabel, modelLabel, subTypeLabel]);
};

const getVehicle =
  (makeOptions) => (path) => (acc, source, baseForm, vehicle) => {
    const value = get(path, source);

    if (value) {
      return set(
        path,
        map(({ form }) => {
          const formValue = get(['vehicle', 0], form);
          const { model, subType } = formValue;

          let formWithoutKey = {};

          if (subType) {
            formWithoutKey = { ...formValue, subType: '' };
          } else if (model) {
            formWithoutKey = { ...formValue, model: null };
          } else {
            formWithoutKey = { ...formValue, make: null };
          }

          const preparedForm = getPreparedForm(
            set(
              ['vehicle', 0, 'form'],
              { vehicle: [{ ...formWithoutKey }] },
              source,
            ),
            baseForm,
          );

          return {
            form: preparedForm,
            label: getVehicleFilterLabel(makeOptions)(form, vehicle),
            name: 'vehicle',
          };
        }, value),
        acc,
      );
    }

    return acc;
  };

const formatBoolLabel = ({ path, value }) => (value ? get(path, labels) : null);

// The trailing `()` applies the default scaffolding — without it `filters` is
// swallowed as the scaffolding argument. Not `source => ...(source)` like the
// sibling mappers, since these also read `baseForm` and `vehicle` from `...extra`.
export default (makeOptions = []) =>
  getMapper(
    [
      moveCheckboxList('airbag'),
      moveCheckboxList('bodyType'),
      moveCheckboxList('color'),
      moveCheckboxList('comfort'),
      movePath('consumption', defaultLabelFormatter, {
        name: 'consumptionRange',
      }),
      movePath('doors', defaultLabelFormatter, { name: 'doorRange' }),
      moveCheckboxList('extras'),
      movePath('emissionSticker'),
      movePath('envStandard'),
      moveCheckboxList('fuelType'),
      movePathBy('geoLocation', getGeoLocation),
      moveCheckboxList('light'),
      moveCheckboxList('interior'),
      movePath('metallic'),
      moveRange('mileageRangeMin'),
      moveRange('mileageRangeMax'),
      moveCheckboxList('multimedia'),
      moveCheckboxList('parkingSensors'),
      moveRange('powerRangeMin'),
      moveRange('powerRangeMax'),
      moveRange('powerRangeMax'),
      moveRange('monthlyPriceRangeMin'),
      moveRange('monthlyPriceRangeMax'),
      movePath('prevOwner'),
      movePath('notDamaged', formatBoolLabel, { name: 'notDamaged' }),
      movePath('smoker', formatBoolLabel, { name: 'smoker' }),
      movePath('hasImages', formatBoolLabel, { name: 'hasImages' }),
      moveRange('priceRangeMin'),
      moveRange('priceRangeMax'),
      moveCheckboxList('seatComfort'),
      movePath('seats', defaultLabelFormatter, { name: 'seatRange' }),
      moveCheckboxList('security'),
      moveCheckboxList('serviceHistory'),
      movePath(['distance']),
      movePath('sellerType'),
      moveCheckboxList('transmission'),
      moveCheckboxList('upholstery'),
      moveRange('yearRangeMin'),
      moveRange('yearRangeMax'),
      movePathBy('vehicle', getVehicle(makeOptions)),
    ],
    {},
  )();
